import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { Settings } from '../types';
import { INITIAL_SETTINGS } from '../constants';

export const useSettings = () => {
    const [settings, setSettings] = useState<Settings>(INITIAL_SETTINGS);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settingsDocRef = doc(db, "settings", "global");
                const settingsDoc = await getDoc(settingsDocRef);

                if (settingsDoc.exists()) {
                    setSettings(settingsDoc.data() as Settings);
                } else {
                    // If no settings in DB, create them from initial settings
                    await setDoc(settingsDocRef, INITIAL_SETTINGS);
                    setSettings(INITIAL_SETTINGS);
                }
            } catch (err) {
                console.error("Error fetching settings:", err);
                setError(err as Error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const updateSettings = useCallback(async (newSettings: Settings) => {
        try {
            await setDoc(doc(db, 'settings', 'global'), newSettings);
            setSettings(newSettings);
        } catch (err) {
            console.error("Error updating settings:", err);
            setError(err as Error);
        }
    }, []);

    return { settings, updateSettings, isLoading, error };
};
